/**
 * <MegaMenu> — hover/tap dropdown for marketing Navbar "Features".
 *
 * Desktop: full-width panel with 3 categories side-by-side, each row shows
 *          an icon + feature name + 1-liner. Opens on hover, navigates on click.
 * Mobile:  same data rendered as a vertical accordion inside the Navbar drawer.
 *
 * Data source: featureGroups + featurePages from /data/featuresContent.js
 */
import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { featurePages, featureGroups } from '../data/featuresContent';
import { track } from '../services/analytics';

const ACCENT_TEXT = {
  green: 'text-green-700', blue: 'text-blue-700', orange: 'text-orange-700',
  purple: 'text-purple-700', amber: 'text-amber-700',
};
const ACCENT_BG = {
  green: 'bg-green-50', blue: 'bg-blue-50', orange: 'bg-orange-50',
  purple: 'bg-purple-50', amber: 'bg-amber-50',
};

const onItemClick = (slug) => {
  track('cta_megamenu_feature_clicked', { slug, surface: 'navbar' });
};

/* ---------- Desktop ---------- */
export const MegaMenuDesktop = () => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);

  const handleEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    <div
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      data-testid="megamenu-desktop"
    >
      <Link
        to="/features"
        className="flex items-center gap-1 text-gray-700 hover:text-blue-600 transition-colors text-sm font-medium py-2"
        data-testid="megamenu-trigger"
        onClick={() => track('cta_megamenu_trigger_clicked', { surface: 'navbar' })}
      >
        Features
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </Link>

      {open && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[860px] z-50"
          data-testid="megamenu-panel"
        >
          <div className="bg-white rounded-2xl shadow-2xl ring-1 ring-gray-200 overflow-hidden">
            <div className="grid grid-cols-3 gap-0">
              {featureGroups.map((group) => (
                <div key={group.label} className="p-6 border-r last:border-r-0 border-gray-100">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-3">
                    {group.label}
                  </p>
                  <ul className="space-y-1">
                    {group.items.map((slug) => {
                      const f = featurePages[slug];
                      if (!f) return null;
                      const Icon = f.bullets[0]?.icon;
                      const textCls = ACCENT_TEXT[f.heroAccent] || 'text-gray-700';
                      const bgCls   = ACCENT_BG[f.heroAccent]   || 'bg-gray-50';
                      return (
                        <li key={slug}>
                          <Link
                            to={`/features/${slug}`}
                            onClick={() => onItemClick(slug)}
                            data-testid={`megamenu-item-${slug}`}
                            className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors group"
                          >
                            <div className={`w-8 h-8 rounded-md ${bgCls} flex items-center justify-center flex-shrink-0`}>
                              {Icon && <Icon size={15} className={textCls} />}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-900 group-hover:text-blue-700 transition-colors leading-tight">
                                {f.eyebrow}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5 leading-snug line-clamp-2">
                                {f.subtitle}
                              </p>
                            </div>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>

            <div className="bg-gray-50 px-6 py-3 border-t border-gray-100 flex items-center justify-between">
              <p className="text-xs text-gray-600">
                One platform. Every PG module. No add-on pricing.
              </p>
              <Link
                to="/features"
                onClick={() => track('cta_megamenu_seeall_clicked')}
                data-testid="megamenu-see-all"
                className="text-xs font-semibold text-blue-700 hover:text-blue-800 inline-flex items-center gap-1"
              >
                See all features <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------- Mobile (accordion) ---------- */
export const MegaMenuMobile = ({ onNavigate }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border-y border-gray-100" data-testid="megamenu-mobile">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between py-3 text-gray-700 font-medium"
        data-testid="megamenu-mobile-trigger"
      >
        <span>Features</span>
        <ChevronDown size={16} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="pb-3 space-y-4">
          {featureGroups.map((group) => (
            <div key={group.label}>
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">
                {group.label}
              </p>
              <ul className="space-y-0.5">
                {group.items.map((slug) => {
                  const f = featurePages[slug];
                  if (!f) return null;
                  return (
                    <li key={slug}>
                      <Link
                        to={`/features/${slug}`}
                        onClick={() => { onItemClick(slug); onNavigate?.(); }}
                        data-testid={`megamenu-mobile-item-${slug}`}
                        className="block px-2 py-1.5 text-sm text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded"
                      >
                        {f.eyebrow}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
          <Link
            to="/features"
            onClick={() => { track('cta_megamenu_seeall_clicked', { viewport: 'mobile' }); onNavigate?.(); }}
            data-testid="megamenu-mobile-see-all"
            className="block text-sm font-semibold text-blue-700 px-2 py-1.5"
          >
            See all features →
          </Link>
        </div>
      )}
    </div>
  );
};
